import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { t, getVatPriceProps } from '@insight/toolkit-utils';
import Currency from '../Currency/Currency';
import Icon from '../Icon/Icon';
import { PDPImage } from '../PDPTabs';
import FallbackImage from '../Image/FallbackImage';
import Loading from '../Loading/Loading';
import {
  CONTRACT_LOADING_MESSAGE,
  CONTRACT_TIMED_OUT_MESSAGE,
} from './constants';
import Tooltip from '../Tooltip/Tooltip';

const AddToCartProtection = ({
  warranty,
  setWarranty,
  priceText,
  selectedWarranty,
  defaultCheckedWarrantyId,
  showDesc,
  showPrice,
  showVAT,
  asyncPrice,
  richRelevanceSSEComplete = true,
  isIPSUserWithContract,
  index,
}) => {
  const {
    name,
    image,
    description,
    price: { price, priceInclVat, currency } = {},
    materialId,
  } = warranty;
  const asyncItem = asyncPrice ? asyncPrice[materialId] : null;
  const finalPrice = asyncItem ? asyncItem?.webPrice : price;
  const isContractTimedOut =
    richRelevanceSSEComplete && !asyncItem && isIPSUserWithContract;
  const isPriceAvailable = isIPSUserWithContract
    ? asyncItem
      ? !asyncItem?.callForPrice
      : false
    : true;
  const shouldShowPrice = showPrice && isPriceAvailable;
  const [isHidden, setIsHidden] = useState(true);
  const hideDesc = () => {
    setIsHidden((prev) => !prev);
  };
  const { exclVatProps, inclVatProps } = getVatPriceProps(showVAT);
  return (
    <div
      className={`o-grid u-1/1 o-grid--reverse c-warranty__box ${selectedWarranty.materialId === materialId && 'is-active'}`}
    >
      <div className="u-1/1 u-1/6@tablet o-grid__item c-warranty__box__image">
        {image ? (
          <PDPImage src={image} alt={name} />
        ) : (
          <FallbackImage src={image} alt={name || 'Product description'} />
        )}
      </div>
      <div className="u-1/1 u-5/6@tablet o-grid__item c-warranty__box__details">
        <div className="o-grid">
          <div className="c-warranty__box__details-option">
            <input
              type="radio"
              id={materialId}
              defaultChecked={defaultCheckedWarrantyId === materialId}
              checked={selectedWarranty.materialId === materialId}
              name="warranty"
              onClick={() => setWarranty(warranty, asyncItem)}
              data-testid={'warranty-select-radio'}
            />
          </div>
          {
            <div className="c-warranty__box__details__info">
              <label
                htmlFor={materialId}
                className="c-warranty__box__details__info-name"
              >
                {name || description}
              </label>
              {isIPSUserWithContract && (
                <div>
                  {isContractTimedOut ? (
                    <div className="u-text-bold u-margin-bot-tiny c-warranty__box__details__contract-name">
                      {t(CONTRACT_TIMED_OUT_MESSAGE)}
                    </div>
                  ) : (
                    <div className="u-text-bold u-margin-bot-tiny c-warranty__box__details__contract-name">
                      <Tooltip
                        isInModal
                        content={
                          asyncItem?.abbreviation || asyncItem?.contractName
                        }
                      >
                        <span>
                          {asyncItem?.abbreviation || asyncItem?.contractName}
                        </span>
                      </Tooltip>
                      {isPriceAvailable && (
                        <span className="c-warranty__box__details__contract-best-price">
                          {t('Best price')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div>
                {shouldShowPrice && (
                  <div>
                    <div
                      className={cn('c-warranty__box__details__info-price', {
                        'c-warranty__box__details__info-price__vat': showVAT,
                      })}
                    >
                      {t(priceText)}
                      <Currency
                        currencyCode={currency}
                        value={finalPrice}
                        {...exclVatProps}
                      />
                      {showVAT && (
                        <>
                          &ensp;|&ensp;
                          <Currency
                            currencyCode={currency}
                            value={priceInclVat}
                            {...inclVatProps}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
                {description && showDesc && (
                  <div>
                    <span
                      className="c-warranty__box__details__info-learnmore"
                      onClick={hideDesc}
                    >
                      <Icon
                        icon={isHidden ? 'arrow-down' : 'arrow-up'}
                        name={isHidden ? 'arrow-down' : 'arrow-up'}
                        aria-label={t(
                          `${isHidden} ? 'arrow-down' : 'arrow-up'`
                        )}
                        title="Learn more"
                      />
                      {t('Learn more')}
                    </span>
                    {!isHidden && (
                      <div className="c-warranty__box__details__info-learnmore__description">
                        {description}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export default AddToCartProtection;

AddToCartProtection.propTypes = {
  warranty: PropTypes.object,
  setWarranty: PropTypes.func,
  priceText: PropTypes.string,
  selectedWarranty: PropTypes.object,
  defaultCheckedWarrantyId: PropTypes.string,
  showPrice: PropTypes.bool,
  showVAT: PropTypes.bool,
  index: PropTypes.number,
};

AddToCartProtection.defaultProps = {
  showPrice: true,
};
